import React from 'react';
import { Collapse, Table, Typography } from 'antd';
import { ExclamationCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { DEFAULT_ANOMALY_CONFIG } from '../../utils/anomalyDetection';
import { HIGH_SPENDER_MARGIN } from './HighSpendersTable';

const { Text } = Typography;

interface FlagRow {
  key: string;
  flag: React.ReactNode;
  where: string;
  trigger: string;
  meaning: string;
}

const {
  strictSalesSimilarityThreshold,
  strictGfsDifferenceThreshold,
  salesSimilarityThreshold,
  gfsDifferenceThreshold,
} = DEFAULT_ANOMALY_CONFIG;

const rows: FlagRow[] = [
  {
    key: 'high-spender',
    flag: <Text strong style={{ color: '#cf1322' }}>High Spenders</Text>,
    where: 'Red box at the top of each tier',
    trigger: `Prior Week GFS% is more than ${HIGH_SPENDER_MARGIN} points above the tier's baseline GFS%.`,
    meaning: 'Spending is high compared with stores of the same sales size. "Over By" shows how far past the limit.',
  },
  {
    key: 'critical',
    flag: (
      <Text strong style={{ color: '#ff4d4f' }}>
        <ExclamationCircleOutlined /> Critical
      </Text>
    ),
    where: 'Red icon next to the store name',
    trigger: `Another store's Prior Week sales are within $${strictSalesSimilarityThreshold}, but this store's GFS% is more than ${strictGfsDifferenceThreshold} point higher.`,
    meaning: 'A near-identical store spent noticeably less. This is the strongest sign of over-ordering or waste. Look at it first.',
  },
  {
    key: 'warning',
    flag: (
      <Text strong style={{ color: '#faad14' }}>
        <WarningOutlined /> Warning
      </Text>
    ),
    where: 'Yellow icon next to the store name',
    trigger: `No store is within $${strictSalesSimilarityThreshold}, but one is within $${salesSimilarityThreshold.toLocaleString()} and this store's GFS% is more than ${gfsDifferenceThreshold} points higher.`,
    meaning: 'A similar-sized store spent less, but the match is looser, so expect more natural variation. Worth a check.',
  },
  {
    key: 'bottom',
    flag: <Text strong style={{ color: '#ff4d4f' }}>#1 – #10</Text>,
    where: 'Red rank next to the store name',
    trigger: 'Top 10 stores company-wide by Prior Week GFS% relative to Prior Week sales.',
    meaning: 'Bottom performers. Lower sales with high GFS% ranks worse. Export with "Export Bottom Performers PDF".',
  },
];

const columns = [
  { title: 'Flag', dataIndex: 'flag', key: 'flag', width: 130 },
  { title: 'Where it shows', dataIndex: 'where', key: 'where', width: 170 },
  { title: 'Triggered when', dataIndex: 'trigger', key: 'trigger' },
  { title: 'What it means', dataIndex: 'meaning', key: 'meaning' },
];

export const FlagGuide: React.FC = () => (
  <Collapse
    style={{ marginBottom: 16 }}
    items={[
      {
        key: 'guide',
        label: (
          <span>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Flag guide: High Spenders, Critical and Warning
          </span>
        ),
        children: (
          <>
            <Table
              columns={columns}
              dataSource={rows}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              GFS% = GFS spend ÷ total sales (Toast + EzCater). "Points" means percentage points:
              going from 9.0% to 10.5% is 1.5 points. Critical and Warning compare against every
              store, not just the same tier. Hover over a flag to see the store it was compared with.
            </Text>
          </>
        ),
      },
    ]}
  />
);
